import { DataGrid } from "@msys/ui";
import {
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  type GridColDef,
  type GridGroupingColDefOverride,
  type GridGroupNode,
  gridRowNodeSelector,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  filterCategories,
  removeSubcategoriesOfDeletedParent,
} from "../../categories/helpers.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import type { CategoryFragment } from "../../categories/useCategories.generated.js";
import {
  activate,
  type CatalogueBuffer,
  deactivate,
  deriveAllTriStates,
  deriveSelectAllState,
  deriveTriState,
  deselectAll,
  matrixCellKey,
  selectAll,
  subtreeIds,
  type TaxonomyIndex,
  toggleAction,
} from "./catalogueModel.js";

interface CatalogueRow {
  id: string;
  name: string;
  path: string[];
}

export interface CatalogueGridProps {
  categories: CategoryFragment[];
  index: TaxonomyIndex;
  buffer: CatalogueBuffer;
  /**
   * Controlled search term. Filtering lives here (it drives the grid rows), but
   * the input itself is owned by the page's `CatalogueToolbar`, so the term is
   * passed in. Empty string shows the full taxonomy.
   */
  search: string;
  projectTypes: readonly OfferedProjectType[];
  /**
   * The subset of `projectTypes` that a cascade activation (activate /
   * select-all) auto-fills on each newly-active leaf. Defaults to all of
   * `projectTypes`. Offered-services surfaces pass `AUTO_FILLED_PROJECT_TYPES`
   * to keep DIY opt-in (not auto-checked) per MEIS-12682.
   */
  autoFillProjectTypes?: readonly OfferedProjectType[];
  onBufferChange: (next: CatalogueBuffer) => void;
  /** Cells (`matrixCellKey`) that carry a buffered template overwrite. */
  overriddenCells?: ReadonlySet<string>;
  /**
   * Display label per overridden cell (`matrixCellKey`) — the resolved template
   * title. Cells overridden with only a smart process (no template) have no
   * entry here and fall back to a generic "Custom" pill label.
   */
  overrideLabels?: ReadonlyMap<string, string>;
  onEditOverride?: (
    categoryId: string,
    projectType: OfferedProjectType,
    anchorEl: HTMLElement
  ) => void;
  /**
   * Group node ids (any depth) to expand by default. Callers pass the non-leaf
   * categories that are "not empty" so declared descendants are visible on
   * entry. Must be a stable reference — a new set on every render makes MUI
   * re-apply defaults and revert the user's manual expand/collapse.
   */
  initiallyExpandedIds?: ReadonlySet<string>;
  /**
   * Render the grid in the read-only view mode. Controls are not merely
   * disabled — they switch to a lighter, non-interactive representation so the
   * table reads as "these are the offered services", not "this table is
   * inactive": an active activation/action renders as a plain primary
   * checkmark (no checkbox), an indeterminate parent as a muted dash, and an
   * inactive/unchecked cell as nothing. The select-all header control is not
   * rendered. Default (non-overridden) offered cells show no pill — the default
   * pill exists only as the edit-mode upload affordance — while a cell with a
   * custom template still shows its pill, as a static, non-clickable label. The
   * search filter stays live — it mutates no data, only which rows are visible.
   */
  readOnly?: boolean;
  /**
   * Whether leaf action cells expose the template-overwrite affordance. The
   * onboarding matrix reuses this grid purely for the offered-services cascade
   * and has no override concept, so it passes `false` to drop the affordance
   * (and the `overriddenCells`/`onEditOverride` props) entirely.
   */
  showOverrides?: boolean;
}

const EMPTY_OVERRIDDEN_CELLS: ReadonlySet<string> = new Set();
const EMPTY_OVERRIDE_LABELS: ReadonlyMap<string, string> = new Map();

export const CatalogueGrid = ({
  categories,
  index,
  buffer,
  search,
  projectTypes,
  autoFillProjectTypes = projectTypes,
  onBufferChange,
  overriddenCells,
  overrideLabels,
  onEditOverride,
  initiallyExpandedIds,
  readOnly = false,
  showOverrides = true,
}: CatalogueGridProps) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { projectTypeLabels } = useProjectTypes();
  const apiRef = useGridApiRef();
  const defaultLabel = t("Default", { ns: "OrganisationProfile" });
  const customLabel = t("Custom", { ns: "OrganisationProfile" });

  const usableCategories = React.useMemo(
    () => removeSubcategoriesOfDeletedParent(categories, categories),
    [categories]
  );

  // `path` is the chain of IDs from root to self — tree-data node identity must
  // be stable and unique, and category names can collide across the taxonomy.
  // The grouping cell renders the name via a custom renderCell. `ancestorsOf`
  // returns nearest-first, so reverse to get root→self.
  const allRows = React.useMemo<CatalogueRow[]>(
    () =>
      usableCategories.map(c => ({
        id: c.id,
        name: c.name,
        path: [...index.ancestorsOf(c.id)].reverse().concat(c.id),
      })),
    [usableCategories, index]
  );

  const rows = React.useMemo<CatalogueRow[]>(() => {
    if (!search.trim()) return allRows;
    const matched = filterCategories(usableCategories, search);
    const visibleIds = new Set(matched.map(c => c.id));
    return allRows.filter(r => visibleIds.has(r.id));
  }, [allRows, usableCategories, search]);

  // Stable identity — a new prop reference makes MUI re-evaluate this for
  // every node, reverting user-driven expansion on unrelated re-renders.
  // `node.groupingKey` is the last path segment, i.e. the category id.
  const isGroupExpandedByDefault = React.useCallback(
    (node: GridGroupNode) =>
      initiallyExpandedIds?.has(String(node.groupingKey)) ?? false,
    [initiallyExpandedIds]
  );

  // An active search filter drops non-matching rows from the grid tree and
  // demotes a group whose descendants are all filtered out to a leaf node.
  // MUI X's setRowChildrenExpansion throws in both cases ("No row with id …
  // found" / "Only group nodes can be expanded"), so expand a row only when it
  // is still present AND still a group. The cascade selection is unaffected —
  // it runs over the full taxonomy index; only the visual expansion is
  // grid-bound.
  const expandRowIfPresent = React.useCallback(
    (id: string) => {
      if (!apiRef.current) return;
      const node = gridRowNodeSelector(apiRef, id);
      if (node?.type === "group") {
        apiRef.current.setRowChildrenExpansion(id, true);
      }
    },
    [apiRef]
  );

  const expandSubtree = React.useCallback(
    (id: string) => {
      for (const sid of subtreeIds(index, id)) {
        if (!index.isLeaf(sid)) {
          expandRowIfPresent(sid);
        }
      }
    },
    [expandRowIfPresent, index]
  );

  const handleToggleNode = React.useCallback(
    (id: string) => {
      const tri = deriveTriState(buffer, index, id);
      if (tri === "checked") {
        onBufferChange(deactivate(buffer, index, id));
      } else {
        onBufferChange(activate(buffer, index, id, autoFillProjectTypes));
        expandSubtree(id);
      }
    },
    [buffer, index, autoFillProjectTypes, onBufferChange, expandSubtree]
  );

  const selectAllState = React.useMemo(
    () => deriveSelectAllState(buffer, index),
    [buffer, index]
  );
  const allDeclared = selectAllState === "checked";
  const someDeclared = selectAllState === "indeterminate";

  const triStates = React.useMemo(
    () => deriveAllTriStates(buffer, index),
    [buffer, index]
  );

  const handleToggleSelectAll = React.useCallback(() => {
    if (allDeclared) {
      onBufferChange(deselectAll(buffer));
    } else {
      onBufferChange(selectAll(buffer, index, autoFillProjectTypes));
      for (const id of index.allIds) {
        if (!index.isLeaf(id)) {
          expandRowIfPresent(id);
        }
      }
    }
  }, [
    expandRowIfPresent,
    buffer,
    index,
    autoFillProjectTypes,
    onBufferChange,
    allDeclared,
  ]);

  const projectTypeColumns = React.useMemo<GridColDef<CatalogueRow>[]>(
    () =>
      projectTypes.map(projectType => ({
        field: projectType,
        headerName: projectTypeLabels[projectType],
        sortable: false,
        groupable: false,
        filterable: false,
        align: "left",
        headerAlign: "left",
        width: 132,
        renderCell: ({ row }) => {
          if (!index.isLeaf(row.id)) return null;
          return (
            <CatalogueActionCell
              row={row}
              projectType={projectType}
              buffer={buffer}
              overriddenCells={overriddenCells ?? EMPTY_OVERRIDDEN_CELLS}
              overrideLabels={overrideLabels ?? EMPTY_OVERRIDE_LABELS}
              onBufferChange={onBufferChange}
              onEditOverride={onEditOverride}
              readOnly={readOnly}
              showOverrides={showOverrides}
              defaultLabel={defaultLabel}
              customLabel={customLabel}
            />
          );
        },
      })),
    [
      projectTypes,
      projectTypeLabels,
      index,
      buffer,
      overriddenCells,
      overrideLabels,
      onBufferChange,
      onEditOverride,
      readOnly,
      showOverrides,
      defaultLabel,
      customLabel,
    ]
  );

  const groupingColDef = React.useMemo<
    GridGroupingColDefOverride<CatalogueRow>
  >(
    () => ({
      headerName: t("Category", { ns: "OrganisationProfile" }),
      flex: 1,
      minWidth: 260,
      hideable: false,
      sortable: false,
      renderHeader: () => {
        const categoryLabel = t("Category", { ns: "OrganisationProfile" });
        // Read-only view has nothing to select-all — show only the label.
        if (readOnly) return <span>{categoryLabel}</span>;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox
              size="small"
              checked={allDeclared}
              indeterminate={someDeclared}
              onChange={handleToggleSelectAll}
              slotProps={{
                input: {
                  "aria-label": t("Select all", { ns: "Global" }),
                },
              }}
            />
            <span>{categoryLabel}</span>
          </Box>
        );
      },
      renderCell: params => {
        const id = String(params.id);
        const name = params.row?.name ?? String(params.value);
        const tri = triStates.get(id) ?? "empty";
        const depth = params.rowNode.depth ?? 0;
        const isGroup = params.rowNode.type === "group";
        const expanded =
          params.rowNode.type === "group"
            ? Boolean(params.rowNode.childrenExpanded)
            : false;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              pl: depth * 3,
            }}
          >
            {readOnly ? (
              <ReadOnlyStateGlyph tri={tri} name={name} />
            ) : (
              <Checkbox
                size="small"
                checked={tri === "checked"}
                indeterminate={tri === "indeterminate"}
                onClick={e => e.stopPropagation()}
                onChange={() => handleToggleNode(id)}
                slotProps={{
                  input: { "aria-label": name },
                }}
              />
            )}
            {isGroup ? (
              <IconButton
                size="small"
                aria-expanded={expanded}
                aria-label={`${
                  expanded
                    ? t("Collapse", { ns: "Global" })
                    : t("Expand", { ns: "Global" })
                } ${name}`}
                onClick={e => {
                  e.stopPropagation();
                  apiRef.current?.setRowChildrenExpansion?.(id, !expanded);
                }}
              >
                {expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}
            <Typography variant="body2" noWrap>
              {name}
            </Typography>
          </Box>
        );
      },
    }),
    [
      t,
      apiRef,
      index,
      buffer,
      readOnly,
      handleToggleNode,
      handleToggleSelectAll,
      allDeclared,
      someDeclared,
      triStates,
    ]
  );

  return (
    <DataGrid
      apiRef={apiRef}
      rows={rows}
      columns={projectTypeColumns}
      treeData
      getTreeDataPath={getTreeDataPath}
      groupingColDef={groupingColDef}
      disableRowSelectionOnClick
      autoHeight
      density="compact"
      hideFooter
      pagination={false}
      disableColumnFilter
      disableColumnMenu
      // Virtualisation needs layout measurements jsdom doesn't provide;
      // taxonomy is ~300 rows so rendering all of them is cheap.
      disableVirtualization
      isGroupExpandedByDefault={isGroupExpandedByDefault}
    />
  );
};

const getTreeDataPath = (row: CatalogueRow) => row.path;

interface CatalogueActionCellProps {
  row: CatalogueRow;
  projectType: OfferedProjectType;
  buffer: CatalogueBuffer;
  overriddenCells: ReadonlySet<string>;
  overrideLabels: ReadonlyMap<string, string>;
  onBufferChange: (next: CatalogueBuffer) => void;
  onEditOverride?: (
    categoryId: string,
    projectType: OfferedProjectType,
    anchorEl: HTMLElement
  ) => void;
  readOnly: boolean;
  showOverrides: boolean;
  defaultLabel: string;
  customLabel: string;
}

const CatalogueActionCell = ({
  row,
  projectType,
  buffer,
  overriddenCells,
  overrideLabels,
  onBufferChange,
  onEditOverride,
  readOnly,
  showOverrides,
  defaultLabel,
  customLabel,
}: CatalogueActionCellProps) => {
  const key = matrixCellKey(row.id, projectType);
  const isDeclared = buffer.declared.has(row.id);
  const isChecked = buffer.selections.has(key);
  const hasOverride = overriddenCells.has(key);

  // Read-only representation: an offered action is a plain primary checkmark
  // (no checkbox); an unchecked or undeclared cell shows nothing. A default
  // (non-overridden) cell shows no pill — the default pill is only the
  // edit-mode upload affordance — while a custom-template cell keeps its pill
  // as a static, non-clickable label. The checkmark sits in a leading slot the
  // size of the edit-mode checkbox and the pill fills the rest, mirroring the
  // edit layout below: checkmarks line up across every row (pill or not) and
  // stay put when toggling between view and edit.
  if (readOnly) {
    if (!isChecked) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box
          sx={{
            width: 34,
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CheckIcon
            fontSize="small"
            color="primary"
            titleAccess={`${row.name} ${projectType}`}
          />
        </Box>
        {showOverrides && hasOverride && (
          <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <OverrideChip
              checked
              hasOverride
              label={overrideLabels.get(key) ?? customLabel}
              readOnly
              ariaLabel={`${row.name} ${projectType} override`}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Checkbox
        size="small"
        disabled={!isDeclared}
        checked={isChecked}
        // Stops DataGrid's cell-click from collapsing the parent tree group.
        onClick={e => e.stopPropagation()}
        onChange={() =>
          onBufferChange(toggleAction(buffer, row.id, projectType))
        }
        slotProps={{
          input: {
            "aria-label": `${row.name} ${projectType}`,
          },
        }}
      />
      {/* Flexible slot so the pill fills the remaining cell width and truncates
          rather than pushing the checkbox around. Only declared cells expose the
          override pill; an undeclared cell has no offered service to override. */}
      {showOverrides && isDeclared && (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
          <OverrideChip
            checked={isChecked}
            hasOverride={hasOverride && isChecked}
            label={
              isChecked && hasOverride
                ? (overrideLabels.get(key) ?? customLabel)
                : defaultLabel
            }
            readOnly={readOnly}
            onClick={
              onEditOverride
                ? anchorEl => onEditOverride(row.id, projectType, anchorEl)
                : undefined
            }
            ariaLabel={`${row.name} ${projectType} override`}
          />
        </Box>
      )}
    </Box>
  );
};

interface ReadOnlyStateGlyphProps {
  tri: "checked" | "indeterminate" | "empty";
  name: string;
}

// Read-only stand-in for the tri-state activation checkbox, sized to the small
// checkbox's footprint so category names stay aligned across checked, partial,
// and empty rows. A checked category is a plain primary checkmark, a partially
// offered parent a muted dash, an empty category nothing at all.
const ReadOnlyStateGlyph = ({ tri, name }: ReadOnlyStateGlyphProps) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {tri === "checked" && (
      <CheckIcon fontSize="small" color="primary" titleAccess={name} />
    )}
    {tri === "indeterminate" && (
      <RemoveIcon
        fontSize="small"
        sx={{ color: "text.disabled" }}
        titleAccess={name}
      />
    )}
  </Box>
);

interface OverrideChipProps {
  /** The cell's action is ticked — an override is only meaningful when so. */
  checked: boolean;
  /** The cell carries a saved/buffered override (implies `checked`). */
  hasOverride: boolean;
  label: string;
  readOnly: boolean;
  onClick?: (anchorEl: HTMLElement) => void;
  ariaLabel: string;
}

// Two designs: an overridden cell shows a filled, tinted pill labelled with the
// template title (or "Custom"); a non-overridden cell shows an outlined
// "Default" pill with a swap icon. An unticked cell shows a disabled Default
// pill — its override, if any, won't persist, so it reads as not-yet-set.
const OverrideChip = ({
  checked,
  hasOverride,
  label,
  readOnly,
  onClick,
  ariaLabel,
}: OverrideChipProps) => {
  const clickable = checked && !readOnly && onClick !== undefined;
  const handleClick = clickable
    ? (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onClick?.(e.currentTarget);
      }
    : undefined;

  // The Default pill trails a swap icon (embedded in the label so it sits after
  // the text — MUI's `icon` prop would render it on the left). The overridden
  // pill is icon-less.
  const labelNode = hasOverride ? (
    label
  ) : (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {label}
      <SwapHorizIcon sx={{ fontSize: 16 }} />
    </Box>
  );

  const chip = (
    <Chip
      size="small"
      label={labelNode}
      variant={hasOverride ? "filled" : "outlined"}
      color={hasOverride ? "primary" : "secondary"}
      disabled={!checked}
      onClick={handleClick}
      aria-label={ariaLabel}
      sx={{
        maxWidth: "100%",
        cursor: clickable ? "pointer" : "default",
      }}
    />
  );

  // A tooltip surfaces the full (possibly truncated) label. Skip it for a
  // disabled/unticked pill — its "Default" label never truncates and a disabled
  // element swallows the hover anyway.
  return checked && (clickable || readOnly) ? (
    <Tooltip title={label}>{chip}</Tooltip>
  ) : (
    chip
  );
};
